/**
 * ⚠️ DUPLICATE COMPONENT WARNING ⚠️
 *
 * This component (ResultDropdown) is the NEW version of a duplicated component.
 * The legacy version is TypeaheadOverlay located at:
 * `/packages/mirage/packages/search/SearchResults/TypeaheadOverlay.tsx`
 *
 * REQUIRED ACTION FOR DEVELOPERS:
 * Any changes made to this file MUST be mirrored in the other file to maintain
 * consistent behavior across both implementations.
 *
 * WHY THIS MATTERS:
 * These components serve the same purpose but are conditionally used based on the
 * feature flag: `dash_2025_03_18_omnibox`
 *
 * Failing to synchronize changes will create inconsistent user experiences
 * depending on whether a user has the feature flag enabled or disabled.
 */

/**
 * ResultOverlay is an omnibox-specific TypeaheadOverlay
 */
import "../variables.module.css";

import type { OverlayProps } from "@dropbox/dig-components/overlay";
import { Overlay } from "@dropbox/dig-components/overlay";
import { useScrollToTypeaheadItem } from "@mirage/search/hooks/useScrollToTypeaheadItem";
import classnames from "classnames";
import React, { useMemo } from "react";

import type { TypeaheadResult } from "@mirage/mosaics/SearchBarWithTypeahead/useConvertToTypeaheadResults";
import { NoSearchResults } from "@mirage/search/General/NoSearchResults";
import { isInsideExtensionSidePanel } from "@mirage/shared/util/webExtensionsHelpers";
import { RoutePath } from "@mirage/webapp/routeTypes";
import { useLocation } from "react-router-dom";
import { useOmniboxMotionFlag } from "../../hooks/useOmniboxMotion";
import { useResultsContainerAnimation } from "../../hooks/useResultsContainerAnimation";
import { TypeaheadInfoPanel } from "../TypeaheadInfoPanel/TypeaheadInfoPanel";
import { DropdownBanner } from "./DropdownBanner";
import style from "./ResultDropdown.module.css";
import { ResultList } from "./ResultList";

export type ResultOverlayProps = {
  isTypeaheadOpen: boolean;
  anchor: React.RefObject<HTMLElement>;
  typeaheadQuery: string;
  results?: Array<TypeaheadResult>;
  selectedIdx?: number;
  /** called with whatever index the user currently has their cursor hovered over */
  setHoveredIdx: (i: number | null) => void;
  onResultClicked: (item: TypeaheadResult, index: number) => void;
  onListItemView: (item: TypeaheadResult, index: number) => void;
  isMouseActive: boolean;
  fullWidth: boolean;
  isAugustStartPageEnabled: boolean;
  isLauncherSidePanelEnabled: boolean;
  isSlimMode: boolean;
  isDualModeLauncher: boolean;
  isDarwin: boolean;
  isInitialQuery?: boolean;
};

export const ResultDropdown = ({
  isTypeaheadOpen,
  anchor,
  results = [],
  selectedIdx = 0,
  setHoveredIdx,
  onResultClicked,
  typeaheadQuery = "",
  onListItemView,
  isMouseActive = true,
  fullWidth = false,
  isAugustStartPageEnabled = false,
  isLauncherSidePanelEnabled = false,
  isSlimMode = false,
  isDualModeLauncher,
  isDarwin,
  isInitialQuery = false,
}: ResultOverlayProps) => {
  const { getMotionVersionFor } = useOmniboxMotionFlag();
  const expandMotionVersion = getMotionVersionFor("expand");

  const { resultItemRefs, overlayContainerRef } = useScrollToTypeaheadItem(
    selectedIdx,
    results,
  );

  const { listContainerRef, bannerRef } = useResultsContainerAnimation({
    isTypeaheadOpen,
    results,
    expandMotionVersion,
    isDualModeLauncher,
    overlayContainerRef,
  });

  const isExtensionSidePanel = isInsideExtensionSidePanel();

  const { pathname } = useLocation();
  const isSERP = pathname === RoutePath.SEARCH_RESULTS;
  const isBannerContentEnabled = !isInitialQuery && !isExtensionSidePanel;
  const hasSideInfoPanel =
    isLauncherSidePanelEnabled &&
    isDualModeLauncher &&
    results.length > 0 &&
    selectedIdx > -1 &&
    selectedIdx < results.length;

  const isExtensionSidePanelSerp = useMemo(
    () => isExtensionSidePanel && isSERP,
    [isExtensionSidePanel, isSERP],
  );

  // Inline the results in the Dual Mode Launcher
  const Container = useMemo(
    () =>
      isDualModeLauncher
        ? (props: React.HTMLAttributes<HTMLDivElement>) => (
            <div
              {...props}
              className={classnames(style.isDualModeLauncher, props.className)}
            />
          )
        : (props: Partial<OverlayProps>) => (
            <Overlay
              placement="bottom"
              anchorRef={anchor}
              setWidthSameAsAnchor={!isExtensionSidePanelSerp}
              boundaryRef={overlayContainerRef}
              // Portals get weird when inside extension side panel
              isPortaled={!isExtensionSidePanel}
              offsetDistance={isExtensionSidePanelSerp ? -8 : undefined}
              {...props}
            />
          ),
    [
      isDualModeLauncher,
      anchor,
      overlayContainerRef,
      isExtensionSidePanelSerp,
      isExtensionSidePanel,
    ],
  );

  if (!isTypeaheadOpen && expandMotionVersion === "OFF") {
    return null;
  }

  return (
    <div
      ref={overlayContainerRef}
      className={classnames(style.overlayContainer, {
        [style.hasSideInfoPanel]: hasSideInfoPanel,
        [style.isExpandMotionV1]: expandMotionVersion === "V1",
      })}
    >
      <Container
        className={classnames(style.overlay, {
          [style.isSlimMode]: isSlimMode,
          [style.isTypeaheadOpen]: isTypeaheadOpen,
          [style.fullWidth]: fullWidth,
          [style.isAugustStartPageEnabled]: isAugustStartPageEnabled,
          [style.isExpandMotionV1]: expandMotionVersion === "V1",
          [style.isInitialQuery]: isInitialQuery,
          [style.isInsideExtensionSidePanel]: isInsideExtensionSidePanel(),
          [style.isSERP]: isSERP,
          [style.isLauncherSidePanelEnabled]: hasSideInfoPanel,
        })}
      >
        <div
          className={classnames(style.animatedTopBorder, {
            [style.isTypeaheadOpen]: isTypeaheadOpen,
            [style.isExpandMotionV1]: expandMotionVersion === "V1",
          })}
        />
        <ResultList
          isTypeaheadOpen={isTypeaheadOpen}
          isDualModeLauncher={isDualModeLauncher}
          expandMotionVersion={expandMotionVersion}
          listContainerRef={listContainerRef}
          results={results}
          selectedIdx={selectedIdx}
          setHoveredIdx={setHoveredIdx}
          onResultClicked={onResultClicked}
          typeaheadQuery={typeaheadQuery}
          isMouseActive={isMouseActive}
          fullWidth={fullWidth}
          resultItemRefs={resultItemRefs}
          onListItemView={onListItemView}
        />
        {results.length === 0 && isDualModeLauncher && (
          <NoSearchResults query={typeaheadQuery} />
        )}
        <div
          ref={bannerRef}
          className={classnames(style.banner, {
            [style.isInsideExtensionSidePanel]: isInsideExtensionSidePanel(),
          })}
        >
          <DropdownBanner
            isDarwin={isDarwin}
            isBannerContentEnabled={isBannerContentEnabled}
            results={results}
            selectedIdx={selectedIdx}
            isTypeaheadOpen={isTypeaheadOpen}
          />
        </div>
      </Container>
      {hasSideInfoPanel && (
        <TypeaheadInfoPanel
          result={results[selectedIdx]}
          query={typeaheadQuery}
        />
      )}
    </div>
  );
};
ResultDropdown.displayName = "ResultDropdown";
